import { useMutation, useQuery } from '@apollo/client'
import { useCallback, useState } from 'react'
import { toast } from 'sonner'
import { v4 as uuidv4 } from 'uuid'
import type { z } from 'zod'

import { CREATE_USER } from '../../../../../../data/mutations/user'
import { DOES_USER_EXIST } from '../../../../../../data/queries/members'
import type { IMembership, IUserExistsReturnData } from '../../../../../../types/user'
import { CreatedVia } from '../../../../../../types/user'
import type { emailPhoneInputSchema } from '../../../shared/email-phone-form'
import type { IAssignDriverInput } from '../../types'

interface IDriverData {
  countryCode: string
  id: string
  isShipmentOwner: boolean
  name: string
  phoneNumber: string
  workspaceID: string | null
}

interface IDriverState {
  addedByWorkspaceID: string
  driver: IDriverData
}

const INITIAL_DRIVER_STATE: IDriverData = {
  countryCode: '+1',
  id: '',
  isShipmentOwner: false,
  name: '',
  phoneNumber: '',
  workspaceID: null
}

/**
 * Custom hook to handle driver search and assignment in a shipment
 */
export const useDriversList = (handleAddDriver: (input: IAssignDriverInput) => Promise<void>, workspaceID: string) => {
  const [userInput, setUserInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [driverWorkspaceList, setDriverWorkspaceList] = useState<IMembership[]>([])
  const [newDriverData, setNewDriverData] = useState<IDriverState>({
    addedByWorkspaceID: workspaceID,
    driver: INITIAL_DRIVER_STATE
  })

  const setLoadingWithToast = (message: string, isError = true) => {
    setLoading(false)
    isError ? toast.error(message) : toast.success(message)
  }

  const [createUser] = useMutation(CREATE_USER, {
    onCompleted: async () => {
      await handleAddDriver(newDriverData)
      setLoading(false)
    },
    onError: (error) => setLoadingWithToast(error.message)
  })

  const { refetch: userExists } = useQuery(DOES_USER_EXIST, {
    fetchPolicy: 'network-only',
    skip: true
  })

  const createDriverData = (
    phoneNumber: string,
    id: string,
    name?: string | null,
    countryCode?: string | null
  ): IDriverState => ({
    addedByWorkspaceID: workspaceID,
    driver: {
      ...INITIAL_DRIVER_STATE,
      countryCode: countryCode || '+1',
      id,
      name: name || '',
      phoneNumber
    }
  })

  const handleAssignDriver = useCallback(
    async (selectedWorkspaceID: string, driverDataOverride?: IDriverState) => {
      try {
        const isShipmentOwner = driverWorkspaceList.some((workspace) => workspace.workspaceID === workspaceID)

        // Use override if provided (to avoid race condition with state updates), otherwise use state
        const baseData = driverDataOverride || newDriverData

        const driverData = {
          ...baseData,
          driver: {
            ...baseData.driver,
            isShipmentOwner,
            workspaceID: selectedWorkspaceID
          }
        }

        await handleAddDriver(driverData)
        setDriverWorkspaceList([])
        setLoading(false)
      } catch (error) {
        setLoadingWithToast(error instanceof Error ? error.message : 'Failed to assign driver')
      }
    },
    [newDriverData, driverWorkspaceList, handleAddDriver, workspaceID]
  )

  const handleNewUser = async (phoneNumber: string) => {
    const id = uuidv4()
    const driverData = createDriverData(phoneNumber, id)
    setNewDriverData({
      addedByWorkspaceID: workspaceID,
      driver: {
        ...INITIAL_DRIVER_STATE,
        ...driverData.driver
      }
    })

    await createUser({
      variables: {
        createdVia: CreatedVia.ChaineWebApp,
        emailOrPhone: phoneNumber,
        userID: id
      }
    })
  }

  const handleExistingUser = async (existingUser: IUserExistsReturnData, driverData: IDriverState) => {
    try {
      const { memberships, id, name, countryCode, phone } = existingUser
      const membershipCount = memberships?.length ?? 0

      if (membershipCount > 1) {
        setDriverWorkspaceList(memberships)
        return
      }

      if (membershipCount === 1 && memberships?.[0]) {
        // Pass driverData directly to avoid race condition with state updates
        await handleAssignDriver(memberships[0].workspaceID, driverData)
        return
      }

      const addDriverPayload =
        driverData.driver.phoneNumber && driverData.driver.id === id
          ? {
              ...driverData,
              driver: {
                ...driverData.driver,
                countryCode: countryCode || driverData.driver.countryCode || '+1',
                name: name || driverData.driver.name
              }
            }
          : createDriverData(phone || '', id, name || '', countryCode || '+1')
      await handleAddDriver(addDriverPayload)
    } catch (error) {
      setLoadingWithToast(error instanceof Error ? error.message : 'Failed to process existing user')
    }
  }

  const handleSubmit = useCallback(
    async (data: z.infer<typeof emailPhoneInputSchema>) => {
      const cleanedEmailPhone = data.cleanedEmailPhone?.trim()
      if (!cleanedEmailPhone) return

      setUserInput(cleanedEmailPhone)
      setLoading(true)

      try {
        const { data: response } = await userExists({ emailOrPhone: cleanedEmailPhone })
        if (!response) return

        const { userExists: existingUser } = response
        if (existingUser) {
          const driverData = createDriverData(
            existingUser.phone || cleanedEmailPhone,
            existingUser.id,
            existingUser.name,
            existingUser.countryCode || '+1'
          )
          setNewDriverData(driverData)
          await handleExistingUser(existingUser, driverData)
        } else {
          await handleNewUser(cleanedEmailPhone)
        }
      } catch (error) {
        setLoadingWithToast(error instanceof Error ? error.message : 'Failed to process request')
      }
    },
    [userExists]
  )

  const resetInviteState = () => {
    setLoading(false)
    setDriverWorkspaceList([])
  }

  return {
    driverWorkspaceList,
    handleAssignDriver,
    handleSubmit,
    loading,
    resetInviteState,
    setUserInput,
    userInput
  }
}
